import {
  TextField,
  Typography,
  Box,
  InputAdornment,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { roleManagementApi } from "apis";
import { RightDto } from "libs/cellera-sdk";
import React, { useEffect, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import CommonPopup, { PopupButton } from "./CommonPopup";

interface CreateRolePopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    roleData: {
      name: string;
      description: string;
      rightsIds: string[];
    },
    roleId?: string
  ) => void;
  isLoading?: boolean;
  editingRoleId?: string;
}

const CreateRolePopup: React.FC<CreateRolePopupProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  editingRoleId,
}) => {
  const { colors } = useCelleraTheme();
  const [permissions, setPermissions] = useState<RightDto[]>([]);
  const [fetching, setFetching] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    rightsIds: [] as string[],
  });
  const [errors, setErrors] = useState({
    name: "",
    description: "",
    rightsIds: "",
  });

  useEffect(() => {
    setFetching(true);
    const fetchPermissions = async () => {
      try {
        const permissions = await roleManagementApi.getRights();
        setPermissions(permissions);
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
      } finally {
        setFetching(false && !editingRoleId);
      }
    };
    fetchPermissions();
    if (editingRoleId) {
      const fetchRole = async () => {
        try {
          const role = await roleManagementApi.getRoleById({
            id: editingRoleId,
          });
          setFormData({
            name: role?.name || "",
            description: role?.description || "",
            rightsIds:
              role?.rights?.map((right) => right.id) || ([] as string[]),
          });
        } catch (error) {
          console.error("Failed to fetch role:", error);
        }
        setFetching(false);
      };
      fetchRole();
    } else {
      setFetching(false);
      setFormData({
        name: "",
        description: "",
        rightsIds: [] as string[],
      });
    }
  }, [editingRoleId]);

  const validateForm = () => {
    const newErrors = {
      name: "",
      description: "",
      rightsIds: "",
    };

    if (!formData.name.trim()) {
      newErrors.name = "Role name is required";
    }

    if (formData.description.length > 2000) {
      newErrors.description = "Description must be 2000 characters or less";
    }

    if (formData.rightsIds.length === 0) {
      newErrors.rightsIds = "At least one permission is required";
    }

    setErrors(newErrors);
    return !newErrors.name && !newErrors.description;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData, editingRoleId);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", description: "", rightsIds: [] });
    setErrors({ name: "", description: "", rightsIds: "" });
    onClose();
  };

  const buttons: PopupButton[] = [
    {
      label: "Cancel",
      onClick: handleClose,
      variant: "outlined",
      disabled: isLoading,
      sx: {
        borderColor: colors.border.main,
        borderRadius: "80px",
        width: "150px",
        backgroundColor: colors.background.secondary,
        color: colors.text.primary,
        "&:hover": {
          borderColor: colors.border.main,
          backgroundColor: colors.background.highlight,
          color: colors.text.primary,
        },
      },
    },
    {
      label: editingRoleId ? "Save" : "Create Role",
      onClick: handleSubmit,
      variant: "contained",
      disabled: isLoading || !formData.name.trim(),
      loading: isLoading,
      sx: {
        borderColor: colors.border.main,
        borderRadius: "80px",
        width: "150px",
        color: colors.text.inverse,
        backgroundColor: colors.primary.main,
        "&:hover": {
          borderColor: colors.border.main,
          backgroundColor: colors.background.highlight,
          color: colors.text.primary,
        },
      },
    },
  ];

  return (
    <CommonPopup
      open={open}
      onClose={handleClose}
      actionsSx={{
        p: "16px",
      }}
      title={
        editingRoleId
          ? "Edit Role & Permissions"
          : "Create New Role & Permissions"
      }
      buttons={buttons}
      maxWidth="md"
      minHeight={400}
      fullWidth
    >
      {fetching ? (
        <Box className="flex justify-center items-center h-full">
          <CircularProgress />
        </Box>
      ) : (
        <Box className="space-y-6">
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: colors.text.primary,
                fontSize: "16px",
                fontWeight: 500,
                mb: 1,
                mt: 2,
              }}
            >
              Role Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Role Name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              error={!!errors.name}
              helperText={errors.name}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: colors.border.light,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.border.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary.main,
                  },
                },
                "& .MuiInputBase-input": {
                  color: colors.text.primary,
                  fontSize: "14px",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: colors.text.secondary,
                  opacity: 1,
                },
              }}
            />
          </Box>

          {/* Role Description Field */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: colors.text.primary,
                fontSize: "16px",
                fontWeight: 500,
                mb: 1,
                mt: 2,
              }}
            >
              Role Description
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter Role Description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              error={!!errors.description}
              helperText={errors.description}
              variant="outlined"
              size="small"
              slotProps={{
                htmlInput: {
                  maxLength: 2000,
                },
                input: {
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ alignSelf: "flex-end", mb: 1 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontSize: "12px",
                        }}
                      >
                        {formData.description.length}/2000
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  alignItems: "flex-start",
                  "& fieldset": {
                    borderColor: colors.border.light,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.border.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary.main,
                  },
                },
                "& .MuiInputBase-input": {
                  color: colors.text.primary,
                  fontSize: "14px",
                  resize: "vertical",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: colors.text.secondary,
                  opacity: 1,
                },
                "& .MuiInputBase-input::-webkit-resizer": {
                  display: "block",
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{
                color: colors.text.primary,
                fontSize: "16px",
                fontWeight: 500,
                mb: 1,
                mt: 2,
              }}
            >
              Select Permissions
            </Typography>
            <Select
              multiple
              autoWidth
              labelId="select-permissions-label"
              id="select-permissions"
              value={formData.rightsIds}
              SelectDisplayProps={{
                "aria-placeholder": "Select Permissions",
              }}
              displayEmpty={true}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return (
                    <span style={{ color: colors.text.secondary }}>
                      Select Permissions
                    </span>
                  );
                }
                const text = selected
                  .map((id) => {
                    const permission = permissions.find((p) => p.id == id);
                    return permission?.name;
                  })
                  .join(", ");
                return (
                  <Tooltip title={text}>
                    <span>{text}</span>
                  </Tooltip>
                );
              }}
              sx={{
                "& .MuiSelect-select": {
                  color: colors.text.primary,
                  fontSize: "14px",
                },
                "& .MuiSelect-select:focus": {
                  borderColor: colors.primary.main,
                },
                "& .MuiSelect-select:hover": {
                  borderColor: colors.primary.main,
                },
                width: 400,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    borderRadius: 0,
                    border: `1px solid ${colors.border.light}`,
                    width: 400,
                  },
                },
              }}
            >
              {permissions.map((permission: RightDto) => (
                <MenuItem
                  key={permission.id}
                  value={permission.id}
                  onClick={() => {
                    const isChecked = formData.rightsIds.includes(
                      permission.id
                    );
                    setFormData((prev) => ({
                      ...prev,
                      rightsIds: isChecked
                        ? prev.rightsIds.filter((id) => id !== permission.id)
                        : [...prev.rightsIds, permission.id],
                    }));
                  }}
                >
                  <Checkbox
                    key={permission.id}
                    checked={formData.rightsIds.includes(permission.id)}
                  />
                  <ListItemText primary={permission.name} />
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      )}
    </CommonPopup>
  );
};

export default CreateRolePopup;
